// Provider hours come from EHI televisit provider-configs; slots are computed in the browser.
import { api } from "@/lib/api";

export const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
export const DAY_LABEL = { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" };
const JS_TO_KEY = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
export const weekdayKey = (dateStr) => JS_TO_KEY[new Date(`${dateStr}T00:00:00`).getDay()];

// Default working template offered before a provider has saved hours (Mon–Fri 9–5).
export const defaultWeekly = () => ({
  mon: [{ start: "09:00", end: "17:00" }], tue: [{ start: "09:00", end: "17:00" }],
  wed: [{ start: "09:00", end: "17:00" }], thu: [{ start: "09:00", end: "17:00" }],
  fri: [{ start: "09:00", end: "17:00" }], sat: [], sun: [],
});

export async function getProviderConfig(clientId, npi) {
  const { data } = await api.get("/api/edi/televisit/provider-configs", { params: { client_id: String(clientId) } });
  const c = (data?.configs || []).find((x) => String(x.npi) === String(npi));
  return c ? { ...c, weekly: c.weekly_hours || {}, slot_minutes: c.slot_duration_minutes || 30, buffer_minutes: c.buffer_minutes || 0 } : null;
}

// ---- shared visit types (upstream client.tov_list) ----
export async function getClientTovList(clientId) {
  const { data } = await api.get("/api/edi/note-to-claim/clients");
  const c = (data?.clients || []).find((x) => String(x.client_id) === String(clientId));
  return { client: c || null, list: Array.isArray(c?.tov_list) ? c.tov_list : [] };
}
export async function saveClientTovList(clientId, list) {
  const { data } = await api.patch(`/api/edi/note-to-claim/clients/${clientId}`, { client_id: String(clientId), tov_list: list });
  return data;
}

// ---- slot computation (pure, browser-local) ----
const ymd = (d) => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`; };
const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const ENDED = new Set(["cancelled", "canceled", "no_show"]);

function apptMatchesProvider(a, npi, providerName) {
  const an = a.npi || a.provider_npi || a.rendering_npi;
  if (npi && an && String(an) === String(npi)) return true;
  if (providerName && a.provider_name && norm(a.provider_name) === norm(providerName)) return true;
  return false;
}

// config: {weekly, slot_minutes, buffer_minutes}; dateStr "YYYY-MM-DD".
// Returns [{ start:Date, end:Date, label, available, reason }].
export function buildDaySlots(config, dateStr, appointments, { npi, providerName } = {}) {
  if (!config || !dateStr) return [];
  const key = weekdayKey(dateStr);
  const windows = (config.weekly && config.weekly[key]) || [];
  const slotMin = Number(config.slot_minutes) || 30;
  const buf = Math.max(0, Number(config.buffer_minutes) || 0);
  const now = new Date();
  const booked = (appointments || [])
    .filter((a) => !ENDED.has(a.status) && ymd(a.scheduled_at) === dateStr && apptMatchesProvider(a, npi, providerName))
    .map((a) => { const s = new Date(a.scheduled_at); return { start: s, end: new Date(s.getTime() + (Number(a.duration_minutes) || slotMin) * 60000) }; });

  const out = [];
  for (const w of windows) {
    const s = new Date(`${dateStr}T${w.start}:00`);
    const e = new Date(`${dateStr}T${w.end}:00`);
    let cur = s.getTime();
    const step = (slotMin + buf) * 60000;
    while (cur + slotMin * 60000 <= e.getTime() + 1) {
      const start = new Date(cur);
      const end = new Date(cur + slotMin * 60000);
      const taken = booked.some((b) => start < b.end && end > b.start);
      const past = start < now;
      out.push({
        start, end,
        label: start.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }),
        available: !taken && !past,
        reason: taken ? "booked" : past ? "past" : "",
      });
      cur += step;
    }
  }
  out.sort((a, b) => a.start - b.start);
  return out;
}

// Local datetime-local string (what <input type=datetime-local> and the booking payload expect).
export const toLocalInput = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
